// Turn whatever an iOS Shortcut (or any other quick-capture client) sends into
// one Mindfull item. Pure: no Deno or Supabase APIs, so the app's tests import
// it too (src/lib/__tests__/quickCapture.test.ts).
//
// Why this exists: the iOS share sheet hands a Shortcut a string, a list
// (Instagram, TikTok and X share caption text plus a URL), or a rich object
// (a Safari web page). The old endpoint called .trim() on whatever arrived and
// crashed on the last two. Everything here accepts any shape.

import { isPlaceholderTitle, parseSocialUrl } from './socialLinks.ts';
import { decodeHtmlEntities } from './htmlEntities.ts';

/** Fields that carry credentials, never content. */
const SECRET_KEYS = new Set(['token', 'auth_token', 'capture_token', 'email', 'password', 'apikey']);

const MAX_STRINGS = 200;
const MAX_DEPTH = 6;
/** Notes past this are cut, so one paste can't fill the database. */
export const MAX_NOTE_CHARS = 20_000;
/** Below this a note is a sticky, above it a text card (same split as paste). */
const STICKY_MAX_CHARS = 200;

/** Every string inside `value`, depth first, skipping credential fields. */
export function collectStrings(value: unknown, out: string[] = [], depth = 0): string[] {
  if (out.length >= MAX_STRINGS || depth > MAX_DEPTH || value == null) return out;
  if (typeof value === 'string') {
    const s = value.trim();
    if (s) out.push(s);
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    // Not content a person shares
  } else if (Array.isArray(value)) {
    for (const v of value) collectStrings(v, out, depth + 1);
  } else if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (!SECRET_KEYS.has(k.toLowerCase())) collectStrings(v, out, depth + 1);
    }
  }
  return out;
}

const URL_RE = /https?:\/\/[^\s<>"'`]+/gi;

/** http(s) URLs found in `text`, trailing punctuation trimmed, in order, without repeats. */
export function findUrls(text: string): string[] {
  const found: string[] = [];
  for (const raw of text.match(URL_RE) ?? []) {
    let candidate = raw.replace(/[.,;:!?'"’”]+$/, '');
    // Drop a closing bracket that has no opening one inside the URL ("(see https://x.com/a)")
    for (const [open, close] of [['(', ')'], ['[', ']']]) {
      while (candidate.endsWith(close) && candidate.split(open).length < candidate.split(close).length) {
        candidate = candidate.slice(0, -1);
      }
    }
    try {
      const u = new URL(candidate);
      if ((u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.includes('.')) {
        const s = u.toString();
        if (!found.includes(s)) found.push(s);
      }
    } catch {
      // not a URL after all
    }
  }
  return found;
}

export type CaptureDecision =
  | { kind: 'link'; url: string }
  | { kind: 'note'; text: string }
  | { kind: 'empty' };

/**
 * Pick what to save from the strings of a request. A URL anywhere wins (the
 * caption Instagram adds around it is share-sheet noise); explicit `url`
 * fields are looked at first. Otherwise the text becomes a note.
 */
export function decideCapture(input: { urlStrings: string[]; textStrings: string[] }): CaptureDecision {
  for (const s of [...input.urlStrings, ...input.textStrings]) {
    const [first] = findUrls(s);
    if (first) return { kind: 'link', url: first };
  }
  const text = dedupeLines(input.textStrings.length ? input.textStrings : input.urlStrings).join('\n').trim();
  if (text) return { kind: 'note', text: text.slice(0, MAX_NOTE_CHARS) };
  return { kind: 'empty' };
}

/** Shortcuts often send the same text twice (as `text` and as the input itself). */
function dedupeLines(strings: string[]): string[] {
  const seen = new Set<string>();
  return strings.filter((s) => (seen.has(s) ? false : (seen.add(s), true)));
}

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Plain text as TipTap paragraphs: blank lines split paragraphs, single newlines become <br>. */
export function textToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export interface NoteItem {
  item_type: 'sticky' | 'text_card';
  width: number;
  height: number;
  content: Record<string, unknown>;
}

/** A short note is a yellow sticky, a long one a text card, matching what paste makes. */
export function noteItem(text: string): NoteItem {
  if (text.length < STICKY_MAX_CHARS) {
    return { item_type: 'sticky', width: 280, height: 200, content: { text, color: 'yellow' } };
  }
  return { item_type: 'text_card', width: 320, height: 300, content: { body: textToHtml(text) } };
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Server-sent social card fields we pass through untouched (see LinkItemContent in src/lib/linkPreview.ts). */
const SOCIAL_PASSTHROUGH = [
  'mediaType',
  'authorName',
  'authorAvatar',
  'authorFollowers',
  'recentImages',
  'slides',
  'previewSource',
  'previewPending',
  'previewUpdatedAt',
] as const;

/**
 * A link item's content from fetch-link-metadata's answer (or nothing). The
 * server-side twin of mergeLinkMetadata in src/lib/linkPreview.ts: same
 * fields, same rule that a blocked social page's "Instagram" title loses to
 * "Instagram reel by @handle".
 */
export function linkContent(url: string, metadata: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const m = metadata ?? {};
  const social = parseSocialUrl(url);
  const host = hostname(url);
  const fetchedTitle = decodeHtmlEntities(str(m.title));
  const title = social
    ? isPlaceholderTitle(fetchedTitle, social) || fetchedTitle === host
      ? social.title
      : fetchedTitle
    : fetchedTitle || host;
  const fetchedSite = decodeHtmlEntities(str(m.siteName));
  const fetchedType = str(m.contentType);

  const content: Record<string, unknown> = {
    url,
    title,
    description: decodeHtmlEntities(str(m.description)),
    image: str(m.image),
    siteName: social && (!fetchedSite || fetchedSite === host) ? social.platformName : fetchedSite || host,
    favicon: str(m.favicon) || social?.favicon || '',
    contentType: social && (!fetchedType || fetchedType === 'website') ? social.contentType : fetchedType || 'website',
    price: str(m.price) || null,
    screenshot: str(m.screenshot) || null,
  };
  if (typeof m.imageAspectRatio === 'number') content.imageAspectRatio = m.imageAspectRatio;
  if (social) {
    content.platform = social.platform;
    content.authorHandle = str(m.authorHandle) || social.handle;
    for (const key of SOCIAL_PASSTHROUGH) if (m[key] !== undefined && m[key] !== null) content[key] = m[key];
  }
  return content;
}

/** Same 380-wide card the canvas makes; Instagram and TikTok follow the media's shape (src/lib/socialCard.ts). */
export function linkHeight(content: Record<string, unknown>, width = 380): number {
  const social = parseSocialUrl(str(content.url));
  const imageFirst = !!social && (social.platform === 'instagram' || social.platform === 'tiktok') && social.kind !== 'other';
  if (!imageFirst) return content.image ? 280 : 120;
  const mediaType = str(content.mediaType) || (!social!.isPost ? 'profile' : ['reel', 'video', 'short'].includes(social!.kind) ? 'video' : 'image');
  let ratio = 4 / 5;
  if (mediaType !== 'profile') {
    const stored = typeof content.imageAspectRatio === 'number' && content.image ? content.imageAspectRatio : undefined;
    ratio = Math.min(1.91, Math.max(9 / 16, stored && Number.isFinite(stored) ? stored : mediaType === 'video' ? 9 / 16 : 4 / 5));
  }
  return Math.round(width / ratio) + 39;
}

/** Image types every browser draws. HEIC (the iPhone camera default) is not one of them. */
export const DISPLAYABLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif'];

export function isHeic(type: string, name: string): boolean {
  return /image\/hei[cf]/i.test(type) || /\.hei[cf]$/i.test(name);
}

/** Storage-safe file name: keeps the extension, drops anything odd. */
export function safeFileName(name: string, type: string): string {
  const ext = type.split('/')[1]?.replace('jpeg', 'jpg').replace(/[^a-z0-9]/gi, '') || 'bin';
  const base = (name || 'shared')
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'shared';
  return `${base}.${ext}`;
}

/** Card size for an image 280 wide, by its own aspect ratio, clamped like the canvas does. */
export function imageCardSize(size: { width: number; height: number } | null): { width: number; height: number } {
  if (!size) return { width: 280, height: 200 };
  const ratio = Math.min(3, Math.max(1 / 3, size.width / size.height));
  return { width: 280, height: Math.round(280 / ratio) };
}

/** Token sent as `Authorization: Bearer ...`, `x-capture-token`, or a `token` field. */
export function bearer(header: string | null | undefined): string {
  const m = (header ?? '').match(/^\s*Bearer\s+(.+)$/i);
  return m ? m[1].trim() : '';
}

export const CAPTURE_TOKEN_PREFIX = 'mf_';

export function isCaptureToken(token: string): boolean {
  return token.startsWith(CAPTURE_TOKEN_PREFIX) && token.length >= 20;
}
